import { isCustomNumberColumnKey } from "@/lib/custom-number-columns";
import { getColumnDataSources, isKnownColumnKey, type DataDemandSource, getColumnRegistryEntry } from "@/lib/column-registry";
import { BUILDER_OPTIONS } from "@/lib/widgets/registry";
import type { ColumnFilter } from "@/lib/filtering";
import type { ViewConfig } from "@/lib/views";

export type { DataDemandSource } from "@/lib/column-registry";

export const ALL_DATA_DEMAND_SOURCES: DataDemandSource[] = [
  "productsLite",
  "productsFull",
  "orders",
  "attrs",
  "prices",
  "ads",
  "clusters",
  "inventory",
  "costs",
  "suppliers",
  "topQueries",
  "productGroups",
  "clusterStockHistory",
  "clusterOrderFlows",
  "contentRating",
  "turnoverHistory",
  "batches",
  "supplies",
  "finances",
  "promos",
  "wb",
  "actionDiary",
  "stockDays",
  "priceDays",
  "adsDays",
  "deliveryDays",
  "productMoney",
];

const warnedUnknownColumns = new Set<string>();

type RuntimeNumFilter = {
  key: string;
  min?: number | null;
  max?: number | null;
};
type ViewDataNeedsRuntime = {
  sortKey?: string | null;
  attrFilters?: Record<string, string[]>;
  numFilters?: RuntimeNumFilter[];
  columnFilters?: ColumnFilter[];
  promoFilter?: string[];
  includeWidgets?: boolean;
};
type ViewDataNeedsView = Pick<ViewConfig, "id" | "columns" | "attrFilters" | "sortBy" | "filters"> & Partial<Pick<ViewConfig, "groupBy" | "groupSizes" | "hideDeadStock" | "widgetsEnabled" | "widgets" | "summaryMetrics">>;

function isColumnFilterActive(filter: ColumnFilter): boolean {
  if (filter.mode !== "all") return true;
  if (filter.value?.trim()) return true;
  if (filter.min?.trim() || filter.max?.trim()) return true;
  if (filter.selectedValues?.length || filter.excludedValues?.length) return true;
  return false;
}

function isNumFilterActive(filter: RuntimeNumFilter): boolean {
  return filter.min != null || filter.max != null;
}

function viewUsesColumnForRows(
  view: ViewDataNeedsView | null | undefined,
  runtime: ViewDataNeedsRuntime | undefined,
  matchesColumn: (key: string) => boolean,
): boolean {
  if (!view) return false;
  const sortKey = runtime?.sortKey ?? view.sortBy?.key ?? null;
  if (sortKey && matchesColumn(sortKey)) return true;
  if ((view.filters ?? []).some(filter => matchesColumn(filter.key) && isNumFilterActive(filter))) return true;
  if ((runtime?.numFilters ?? []).some(filter => matchesColumn(filter.key) && isNumFilterActive(filter))) return true;
  return (runtime?.columnFilters ?? []).some(filter => matchesColumn(filter.key) && isColumnFilterActive(filter));
}

/**
 * Custom-number values live in a separate user table, not in Ozon data_cache.
 * They may be loaded after the first paint when they are just visible cells,
 * but they must be loaded before row processing when they control sort/filter.
 */
export function viewNeedsCustomNumberValuesBeforeRows(
  view: ViewDataNeedsView | null | undefined,
  runtime?: ViewDataNeedsRuntime,
): boolean {
  return viewUsesColumnForRows(view, runtime, isCustomNumberColumnKey);
}

/**
 * Tasks are side data. They should not make processView rebuild rows just
 * because task cells loaded, unless the current view actually sorts or filters
 * rows by the task column.
 */
export function viewNeedsTaskDataBeforeRows(
  view: ViewDataNeedsView | null | undefined,
  runtime?: ViewDataNeedsRuntime,
): boolean {
  return viewUsesColumnForRows(view, runtime, key => key === "task");
}

export function isKnownDataNeedColumnKey(colKey: string): boolean {
  return isKnownColumnKey(colKey);
}

function warnUnknownColumn(colKey: string, viewId?: string) {
  if (process.env.NODE_ENV === "production") return;
  const warnKey = `${viewId ?? ""}:${colKey}`;
  if (warnedUnknownColumns.has(warnKey)) return;
  warnedUnknownColumns.add(warnKey);
  console.warn(`[frogstat] Unknown view column data source: ${colKey}${viewId ? ` in view ${viewId}` : ""}. Add it to DEFAULT_COLUMNS or a dynamic rule in lib/view-data-needs.ts.`);
}

export function getDataSourcesForColumn(colKey: string): DataDemandSource[] {
  // Через реестр, а не напрямую: `getColumnDataSources` каждый раз создаёт новый
  // Set и прогоняет две регулярки, а реестр держит готовый результат по ключу
  // колонки. Разница видна на отрисовке таблицы — эта функция вызывается по три
  // раза на ячейку (проверка скелетона в трёх местах), то есть тысячи раз на кадр.
  return getColumnRegistryEntry(colKey).dataSources;
}

export function getUnknownViewDataNeedColumns(view: ViewDataNeedsView, runtime?: { sortKey?: string | null }): string[] {
  const unknown = new Set<string>();
  for (const colKey of view.columns ?? []) {
    if (!isKnownDataNeedColumnKey(colKey)) unknown.add(colKey);
  }
  const sortKey = runtime?.sortKey ?? view.sortBy?.key ?? null;
  if (sortKey && !isKnownDataNeedColumnKey(sortKey)) unknown.add(sortKey);
  return [...unknown];
}

function addColumnNeeds(out: Set<DataDemandSource>, colKey: string, viewId?: string) {
  if (!isKnownDataNeedColumnKey(colKey)) {
    warnUnknownColumn(colKey, viewId);
    return;
  }
  for (const source of getDataSourcesForColumn(colKey)) out.add(source);
}

function addShapeNeeds(out: Set<DataDemandSource>, view: ViewDataNeedsView, runtime?: ViewDataNeedsRuntime) {
  if ((view.attrFilters ?? []).some(filter => filter.values.length > 0)) out.add("attrs");
  if (runtime?.attrFilters && Object.values(runtime.attrFilters).some(values => values.length > 0)) out.add("attrs");
  if (runtime?.promoFilter?.length) out.add("promos");
  if (view.hideDeadStock) out.add("orders");
}

function addSortNeeds(out: Set<DataDemandSource>, view: ViewDataNeedsView, runtime?: ViewDataNeedsRuntime) {
  const sortKey = runtime?.sortKey ?? view.sortBy?.key ?? null;
  if (sortKey) addColumnNeeds(out, sortKey, view.id);
}

function addNumFilterNeeds(out: Set<DataDemandSource>, filters: RuntimeNumFilter[] | undefined, viewId?: string) {
  for (const filter of filters ?? []) {
    if (filter.min == null && filter.max == null) continue;
    addColumnNeeds(out, filter.key, viewId);
  }
}

function addAllFilterNeeds(out: Set<DataDemandSource>, view: ViewDataNeedsView, runtime?: ViewDataNeedsRuntime) {
  addNumFilterNeeds(out, view.filters, view.id);
  addNumFilterNeeds(out, runtime?.numFilters, view.id);
  for (const filter of runtime?.columnFilters ?? []) addColumnNeeds(out, filter.key, view.id);
}

function addSummaryMetricNeeds(out: Set<DataDemandSource>, metric: string) {
  switch (metric) {
    case "revenue":
    case "buyouts":
    case "avg_check":
    case "buyout_rate":
    case "returns":
    case "cancels":
    case "commission":
    case "logistics":
    case "orders_today":
    case "revenue_today":
      out.add("finances");
      break;
    case "ad_spend":
    case "ad_orders":
    case "drr":
      out.add("ads");
      break;
    case "total_stock":
    case "out_of_stock":
      out.add("inventory");
      break;
  }
}

function addWidgetMetricNeeds(out: Set<DataDemandSource>, metric: string | undefined) {
  switch (metric) {
    case "revenue":
    case "returns":
    case "avg_check":
    case "buyout_rate":
    case "drr":
      out.add("finances");
      break;
    case "orders":
    case "order_statuses":
      out.add("orders");
      break;
    case "ad_spend":
    case "ad_funnel":
      out.add("ads");
      break;
    case "stock":
    case "warehouses":
    case "alerts":
    case "radar":
      out.add("inventory");
      break;
    case "revenue_orders":
    case "revenue_share":
    case "period_compare":
    case "scatter_revenue_orders":
      out.add("finances");
      out.add("orders");
      break;
  }
}

function addWidgetNeeds(out: Set<DataDemandSource>, view: ViewDataNeedsView) {
  if (view.widgetsEnabled === false) return;
  const widgets = Array.isArray(view.widgets) ? view.widgets : [];
  for (const widget of widgets) {
    const fallbackOption = BUILDER_OPTIONS.find(option => option.def === widget.def);
    addWidgetMetricNeeds(out, widget.metric ?? fallbackOption?.metric);
    addWidgetMetricNeeds(out, widget.metric2 ?? fallbackOption?.metric2);
    if ((widget.breakdown ?? fallbackOption?.breakdown) === "warehouse") out.add("inventory");
  }
}

export function getInitialViewDataNeeds(view: ViewDataNeedsView, runtime?: ViewDataNeedsRuntime): DataDemandSource[] {
  // Строки любой товарной таблицы начинаются с облегчённого каталога. Раньше
  // /api/products/lite был безусловным запросом DataProvider, поэтому даже
  // текстовый документ загружал весь каталог. Теперь это такая же явная
  // потребность, как заказы или остатки.
  const out = new Set<DataDemandSource>(["productsLite"]);
  addShapeNeeds(out, view, runtime);
  addSortNeeds(out, view, runtime);
  addAllFilterNeeds(out, view, runtime);
  return [...out];
}

export function getViewDataNeeds(view: ViewDataNeedsView, runtime?: ViewDataNeedsRuntime): DataDemandSource[] {
  const out = new Set<DataDemandSource>(["productsLite"]);

  for (const colKey of view.columns ?? []) {
    addColumnNeeds(out, colKey, view.id);
  }

  addShapeNeeds(out, view, runtime);
  addSortNeeds(out, view, runtime);
  addAllFilterNeeds(out, view, runtime);

  if ((runtime?.includeWidgets ?? true) && view.widgetsEnabled !== false) {
    for (const metric of view.summaryMetrics ?? []) addSummaryMetricNeeds(out, metric);
    addWidgetNeeds(out, view);
  }

  return [...out];
}
